import { assertNever } from "@@/shared/assert-never";
import { sortBy } from "@@/utilities/sort";
import type { PayRequestEvent } from "../../events/_pay-request.event";
import { firstEventMustBeACreatedEventCheck } from "../../fist-event-must-be-a-created-event-check";
import { missingPayRequestEventCheck } from "../../missing-pay-request-event-check";
import { noEventsForPayRequestCheck } from "../../no-events-for-pay-request-check";
import type { PayRequestId } from "../../pay-request-id";
import type { PayRequestProjection } from "./pay-request.projection";
import { payRequestProjectionZodObject } from "./pay-request.projection";

const payRequestProjectionReducer = (
    payRequestId: PayRequestId,
    events: PayRequestEvent[],
): PayRequestProjection | undefined => {
    // Filter events for the given payRequestId
    // and sort them by version number
    const _events = events
        .filter(event => event.payRequestId === payRequestId)
        .sort(sortBy(event => event._version));

    // Verifiera att det finns givna events för angivet payRequestId
    noEventsForPayRequestCheck(payRequestId, _events);

    // Make sure the first event is PAY_REQUEST_CREATED_EVENT
    firstEventMustBeACreatedEventCheck(payRequestId, _events);

    // Verifiera att det inte finns några luckor mellan versionsnumren
    missingPayRequestEventCheck(payRequestId, _events);

    // Create projection using the events given
    let projection = {
        _id: payRequestId,
        _type: "PAY_REQUEST_PROJECTION",
    } as PayRequestProjection;

    projectionLoop: for (const event of _events) {
        switch (event.type) {
            case "PAY_REQUEST_CREATED_EVENT": {
                const _projection: PayRequestProjection = {
                    _id: event.payRequestId,
                    _type: "PAY_REQUEST_PROJECTION",
                    _createdAt: event._createdAt,
                    _version: event._version,
                    status: "PENDING",
                    paymentStatus: "NOT_INITIATED",
                    recipients: event.data.recipients,
                    price: event.data.price,
                    items: event.data.items,
                    paymentFor: event.paymentFor,
                    paymentEvents: [],
                };
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_CANCELLED_EVENT": {
                if (projection.status !== "PENDING") {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `Pay request ${payRequestId} was not pending when cancelled`,
                    );
                }
                const _projection: PayRequestProjection = {
                    ...projection,
                    _version: event._version,
                    status: "CANCELLED",
                };
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_CANCELLED_EVENT": {
                // Initiated payment was cancelled
                // This is not the same as cancelling the pay request
                const _projection: PayRequestProjection = {
                    ...projection,
                    _version: event._version,
                    paymentStatus: "CANCELLED",
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_EXPIRED_EVENT": {
                // Initiated payment expired
                // This is not the same as if the pay request itself expired
                const _projection: PayRequestProjection = {
                    ...projection,
                    _version: event._version,
                    paymentStatus: "EXPIRED",
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_FULFILLED_EVENT": {
                // Initiated payment was fulfilled
                if (projection.status !== "PENDING") {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `Pay request ${payRequestId} was not pending when fulfilled, it was in a ${projection.status} state`,
                    );
                }
                const _projection: PayRequestProjection = {
                    ...projection,
                    _version: event._version,
                    status: "FULFILLED",
                    paymentStatus: "FULFILLED",
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                // If the payment is fulfilled, we can break the loop
                // as we don't need to process any more events
                // since the payment has already been fulfilled
                break projectionLoop;
            }
            case "PAY_REQUEST_PAYMENT_INITIATED_EVENT": {
                if (projection.status !== "PENDING")
                    return {
                        ...projection,
                        _version: event._version,
                    };
                const _projection: PayRequestProjection = {
                    ...projection,
                    _version: event._version,
                    paymentStatus: "PENDING",
                };
                _projection.paymentEvents.push(event);

                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_FAILED_EVENT": {
                if (projection.status !== "PENDING")
                    return {
                        ...projection,
                        _version: event._version,
                    };
                const _projection: PayRequestProjection = {
                    ...projection,
                    _version: event._version,
                    paymentStatus: "FAILED",
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            default:
                assertNever(event, "Unhandled event type", {
                    valueProp: "type",
                });
        }
    }

    return payRequestProjectionZodObject.parse(projection);
};

export { payRequestProjectionReducer };
